import { throttle } from 'lodash';
import JQueryView, { on } from '@/common/views/jqueryView';

export default class CaseShowView extends JQueryView {
  // 定数
  private static readonly SCROLL_OFFSET = {
    HEADER_MARGIN: 10,
    BANNER_SHOW: 150,
    BANNER_HIDE_BOTTOM: 50,
  } as const;

  // 状態
  private scrollHandler?: () => void;
  private intersectionObserver?: IntersectionObserver;
  private $slider?: JQuery;

  constructor() {
    super('main');

    // サイド INDEX の Intersection Observer 設定
    const $indexLinks = $('.case-fv__index-list a');
    if ($indexLinks.length) {
      this.setupIntersectionObserver($indexLinks);
    }

    this.initFixedBanner();
    this.initRelatedSlider();

    // Turbo でページ遷移時にクリーンアップ
    document.addEventListener('turbo:before-cache', () => {
      this.destroy();
    });
  }

  // ===============================
  // 共通:ヘッダー高さを取得
  // ===============================
  private getHeaderHeight(): number {
    const header = $('header');
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- NaN も 0 へ戻す既存挙動を維持する
    return header.length > 0 ? header.outerHeight() || 0 : 0;
  }

  private setupIntersectionObserver($indexLinks: JQuery): void {
    const headerHeight = this.getHeaderHeight();
    const observerOptions = {
      rootMargin: `-${headerHeight + CaseShowView.SCROLL_OFFSET.HEADER_MARGIN}px 0px -50% 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetId = `#${entry.target.id}`;
          $indexLinks.removeClass('is-active');
          $indexLinks.filter(`[href="${targetId}"]`).addClass('is-active');
        }
      });
    }, observerOptions);
    this.intersectionObserver = observer;

    // 各セクションを監視対象に追加
    $indexLinks.each((_, link) => {
      const targetId = $(link).attr('href');
      if (targetId) {
        const target = $(targetId).get(0);
        if (target) {
          observer.observe(target);
        }
      }
    });
  }

  // ===============================
  // 固定資料バナー(スクロール表示)
  // ===============================
  private initFixedBanner(): void {
    const $fixedBanner = $('.js-fixed-material');
    if (!$fixedBanner.length) return;

    $fixedBanner.addClass('is-hidden');

    const handleScroll = () => {
      if ($fixedBanner.hasClass('is-closed-by-user')) return;

      const scrollY = window.scrollY;
      const threshold =
        document.body.offsetHeight -
        CaseShowView.SCROLL_OFFSET.BANNER_HIDE_BOTTOM;

      const isAtBottom = window.innerHeight + scrollY >= threshold;
      const shouldShow = scrollY > CaseShowView.SCROLL_OFFSET.BANNER_SHOW;

      $fixedBanner.toggleClass('is-at-bottom', isAtBottom);
      $fixedBanner.toggleClass('is-hidden', !shouldShow);
    };

    this.scrollHandler = throttle(handleScroll, 100);

    $(window).on('scroll', this.scrollHandler);
  }

  // ===============================
  // 関連スライダー(slick)
  // ===============================
  private initRelatedSlider(): void {
    this.$slider = $('.js-case-related__slider');
    if (!this.$slider.length) return;

    // 既に初期化されている場合はスキップ
    if (this.$slider.hasClass('slick-initialized')) {
      return;
    }

    this.$slider.slick({
      infinite: true,
      arrows: true,
      dots: false,
      slidesToScroll: 1,
      variableWidth: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            arrows: false,
          },
        },
      ],
    });
  }

  // ===============================
  // サイド INDEX スクロール連動
  // ===============================
  @on({ event: 'click', selector: '.case-fv__index-list a' })
  handleIndexClick(e: JQuery.ClickEvent) {
    e.preventDefault();

    const $link = $(e.currentTarget);
    const targetId = $link.attr('href');
    if (!targetId) return;

    const targetEl = $(targetId).get(0);
    if (!targetEl) return;

    // スクロール位置を計算してスムーズスクロール
    const headerHeight = this.getHeaderHeight();
    const targetPosition =
      targetEl.getBoundingClientRect().top +
      window.pageYOffset -
      headerHeight -
      CaseShowView.SCROLL_OFFSET.HEADER_MARGIN;

    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
  }

  // ×ボタンで閉じる(今回だけ)
  @on({ event: 'click', selector: '.js-fixed-material-close' })
  handleCloseBanner(e: JQuery.ClickEvent) {
    const $btn = $(e.currentTarget);
    const $fixedBanner = $($btn.data('target'));

    if (!$fixedBanner.length) return;

    $fixedBanner.addClass('is-closed-by-user').addClass('is-hidden');
  }

  // ===============================
  // クリーンアップ
  // ===============================
  destroy() {
    if (this.scrollHandler) {
      $(window).off('scroll', this.scrollHandler);
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.$slider?.hasClass('slick-initialized')) {
      this.$slider.slick('unslick');
    }
    super.destroy();
  }
}
